/**
 * CoverageBySourcePanel — pie chart visualising which Source (Prime /
 * each Partner) holds the winning score for what % of requirements.
 *
 * Built on top of the v3.17.0 partner-scoring data so the answer is
 * always trustworthy ("Sub B carries 60% of the bid"). Federal teaming
 * negotiation aid — informs how the Bid Manager structures prime-sub
 * splits.
 *
 * Placement: a card on the Go/No-Go dashboard.
 * v3.17.1.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import { Users, Crown, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Distinct but legible palette. Prime always lands on slot 0 (amber).
const PALETTE = [
  '#d97706', // amber (Prime)
  '#7c3aed', // purple
  '#0891b2', // cyan
  '#16a34a', // emerald
  '#db2777', // pink
  '#ea580c', // orange
  '#0284c7', // sky
  '#65a30d', // lime
  '#9333ea', // violet
  '#dc2626', // red
];

function colorFor(idx, sourceRole) {
  if (sourceRole === 'prime') return PALETTE[0];
  // Skip slot 0 for non-primes so Prime stays amber even when listed deeper.
  return PALETTE[(idx % (PALETTE.length - 1)) + 1];
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 shadow-md rounded-md px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900">{d.source_name}</div>
      <div className="text-slate-500 capitalize">{d.source_role}</div>
      <div className="mt-1 text-slate-700">
        <strong>{d.requirement_count}</strong> requirements
        ({d.percentage}%)
      </div>
      <div className="text-slate-500">Avg score: {d.avg_score}/3</div>
    </div>
  );
}

export default function CoverageBySourcePanel({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/coverage-by-source`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setData(body);
    } catch (e) {
      console.error('coverage-by-source fetch failed', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refetch when a partner-scoring upload changes the picture.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.pursuitId === pursuitId) fetchData();
    };
    window.addEventListener('partner-scoring-changed', handler);
    return () => window.removeEventListener('partner-scoring-changed', handler);
  }, [pursuitId, fetchData]);

  const sources = data?.sources || [];

  return (
    <Card
      className="border-amber-200 shadow-sm"
      data-testid="coverage-by-source-panel"
    >
      <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <Users className="w-5 h-5" />
          Coverage by Source
          {data?.scored_requirements > 0 && (
            <Badge
              variant="outline"
              className="ml-2 bg-amber-100 text-amber-800 border-amber-300"
              data-testid="coverage-by-source-total"
            >
              {data.scored_requirements} / {data.total_requirements} scored
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Which Source (Prime / Partner) is winning the most requirements.
          Useful for teaming-share negotiations — if Sub B holds 60% of
          coverage, the prime-sub split should reflect that.
        </p>
      </CardHeader>

      <CardContent className="p-4">
        {loading ? (
          <Skeleton className="h-56 w-full" />
        ) : !data || sources.length === 0 ? (
          <div
            className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md"
            data-testid="coverage-by-source-empty"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>No scored requirements yet.</strong> Upload the Prime's
              and each Teaming Partner's filled scoring XLSX from the
              Opportunity Intake page to populate this chart.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sources}
                    dataKey="requirement_count"
                    nameKey="source_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    innerRadius={45}
                    paddingAngle={2}
                    label={(e) => `${e.percentage}%`}
                  >
                    {sources.map((s, idx) => (
                      <Cell
                        key={s.source_name}
                        fill={colorFor(idx, s.source_role)}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    height={28}
                    iconSize={10}
                    wrapperStyle={{ fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2" data-testid="coverage-by-source-list">
              {sources.map((s, idx) => {
                const Icon = s.source_role === 'prime' ? Crown : Users;
                return (
                  <div
                    key={s.source_name}
                    className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-md"
                    data-testid={`coverage-by-source-row-${idx}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: colorFor(idx, s.source_role) }}
                      />
                      <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {s.source_name}
                      </span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-slate-900">
                        {s.percentage}%
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {s.requirement_count} reqs · avg {s.avg_score}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
